// Authenticated customer view: list my tickets with live status + a create
// form linked to my account. Detail/threads reuse the public follow-up page
// (the owner receives the same ref+token from the backend).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import type { StylesConfig } from "react-select";
import { brand, colors } from "../../config/theme";
import { getBrandLogo } from "../../config/brandAssets";
import { useTheme } from "../../context/ThemeContext";
import { useUserStore } from "../../store/userStore";
import { useIsReseller } from "../../hooks/useIsReseller";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import {
  getMyTickets,
  createMyTicket,
  getPublicConfig,
  getCompanyUsers,
  getPendingClosure,
  closeMyTicket,
  formatDate,
  type Ticket,
  type PublicConfig,
  type TicketPriority,
  type AffectedUser,
  type PendingClosure,
} from "../../Layouts/service-desk/services/serviceDeskService";
import { SlaBadge, PriorityBadge, ReworkChip } from "../../Layouts/service-desk/components/Badges";
import { apiErrorMessage } from "../../Layouts/service-desk/services/agentsPanel";
import {
  isPendingClosureLimitError,
  statusFallback,
  PENDING_CLOSURE_STATUS,
} from "../../Layouts/service-desk/services/ticketClosure";
import { MODULE_OPTIONS } from "../../Layouts/service-desk/moduleOptions";
import { INTERNAL_COMPANY_NAME } from "../../Layouts/service-desk/internalCompany";

// Con StylesConfig a secas react-select infiere Option = unknown y onChange
// recibe {} (sin .value): las opciones de esta página son { value, label }.
const getSelectStyles = (isDarkMode: boolean): StylesConfig<{ value: string; label: string }, false> => ({
  control: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.card : colors.light.card,
    borderColor: isDarkMode ? colors.dark.border : colors.light.border,
    borderRadius: "0.5rem",
    minHeight: "36px",
    boxShadow: "none",
    fontSize: "0.875rem",
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    "&:hover": {
      borderColor: isDarkMode ? colors.dark.border : colors.light.borderSecondary,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    fontSize: "0.875rem",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.secondary : colors.text.light.subtle,
    fontSize: "0.875rem",
  }),
  input: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    margin: "0",
    padding: "0",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.card : colors.light.card,
    borderRadius: "0.5rem",
    marginTop: "0.25rem",
    border: isDarkMode ? `1px solid ${colors.dark.border}` : `1px solid ${colors.light.border}`,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0.5rem",
    maxHeight: "400px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? isDarkMode
        ? colors.dark.border
        : colors.light.border
      : state.isFocused
        ? isDarkMode
          ? colors.dark.border
          : colors.light.hover
        : "transparent",
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    cursor: "pointer",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.375rem",
    fontSize: "0.75rem",
    marginBottom: "0.125rem",
    "&:last-child": {
      marginBottom: 0,
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.secondary : colors.text.light.muted,
    padding: "0 0.5rem",
    "&:hover": {
      color: isDarkMode ? colors.text.dark.primary : colors.text.light.secondary,
    },
  }),
});

export default function MisTicketsPage() {
  const { isDarkMode } = useTheme();
  const selectStyles = getSelectStyles(isDarkMode);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useUserStore((state) => state.user);
  const { isReseller } = useIsReseller();
  const { allCompanies, fetchAllCompanies } = useCompaniesStore();
  const [form, setForm] = useState({
    brand: "",
    title: "",
    description: "",
    category: "",
    module: "",
    moduleSection: "",
    priority: "medium" as TicketPriority,
    // Compañía y usuario afectado son obligatorios (#344). Para quien no es
    // admin reseller la compañía es la suya: el backend la fija igualmente.
    companyId: "",
    affectedUserId: "",
  });
  const [companyUsers, setCompanyUsers] = useState<AffectedUser[]>([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  // Cierre por solicitante (#729): con 3+ tickets resueltos sin cerrar el
  // backend rechaza el alta. null = sin dato; entonces no se bloquea nada aquí
  // y decide el backend al crear.
  const [pending, setPending] = useState<PendingClosure | null>(null);
  const [closingRef, setClosingRef] = useState<string | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const createBlocked = Boolean(pending?.blocked);

  const load = () =>
    getMyTickets()
      .then((list) => setTickets(Array.isArray(list) ? list : []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));

  const loadPending = () =>
    getPendingClosure()
      .then(setPending)
      .catch(() => setPending(null));

  const closeTicket = async (reference: string) => {
    setCloseError(null);
    setClosingRef(reference);
    try {
      await closeMyTicket(reference);
      await Promise.all([load(), loadPending()]);
    } catch (err: unknown) {
      setCloseError(apiErrorMessage(err) || "No se pudo cerrar el ticket.");
    } finally {
      setClosingRef(null);
    }
  };

  useEffect(() => {
    load();
    loadPending();
    getPublicConfig().then((c) => {
      setConfig(c);
      setForm((f) => ({ ...f, brand: c.brands[0]?.id || "", category: c.categories[0]?.id || "" }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isReseller) fetchAllCompanies();
  }, [isReseller, fetchAllCompanies]);

  // Compañía por defecto: la del usuario (la del store manda sobre
  // localStorage, que otras pantallas sobrescriben).
  useEffect(() => {
    const ownCompanyId = user?.company_id || localStorage.getItem("userCompanyId") || "";
    if (ownCompanyId) setForm((f) => (f.companyId ? f : { ...f, companyId: ownCompanyId }));
  }, [user]);

  // Usuarios de la compañía elegida. Al cambiar de compañía se vacía la lista
  // de inmediato para no ofrecer usuarios de la compañía anterior mientras carga.
  useEffect(() => {
    setCompanyUsers([]);
    if (!form.companyId) return;
    let cancelled = false;
    setCompanyUsersLoading(true);
    getCompanyUsers(form.companyId)
      .then((list) => {
        if (!cancelled) setCompanyUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCompanyUsers([]);
      })
      .finally(() => {
        if (!cancelled) setCompanyUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.companyId]);

  const affectedUser = companyUsers.find((u) => u.id === form.affectedUserId) || null;
  const userLabel = (u: AffectedUser) => (u.email ? `${u.name} (${u.email})` : u.name);
  const companyLabel = (companyId: string) => {
    const c = allCompanies.find((x) => x.id === companyId);
    return c?.name || c?.business_name || "";
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.title || !form.brand) { setError("Completa marca y asunto."); return; }
    if (!form.companyId) {
      setError(`Selecciona la compañía del ticket. Si es personal interno, usa la compañía ${INTERNAL_COMPANY_NAME}.`);
      return;
    }
    if (!form.affectedUserId) { setError("Selecciona el usuario afectado."); return; }
    setCreating(true);
    try {
      await createMyTicket({ ...form, email: "", files });
      setShowForm(false);
      setForm((f) => ({ ...f, title: "", description: "", module: "", moduleSection: "", affectedUserId: "" }));
      setFiles([]);
      await load();
    } catch (err: unknown) {
      // Llegó al tope mientras llenaba el formulario: se cierra y queda el
      // aviso con los tickets por cerrar, que es lo único que puede hacer.
      if (isPendingClosureLimitError(err)) {
        setShowForm(false);
        await Promise.all([load(), loadPending()]);
        return;
      }
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      setError(typeof msg === "string" && msg ? msg : "No se pudo crear el ticket.");
    } finally {
      setCreating(false);
    }
  };

  const cardCls = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const textCls = isDarkMode ? "text-white" : "text-gray-900";

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"} px-4 py-8`}>
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={getBrandLogo("main")} alt="Logo" className="h-8 w-auto" />
            <h1 className={`text-xl font-bold ${textCls}`}>Mis tickets</h1>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            disabled={createBlocked}
            title={createBlocked ? "Cierra tus tickets resueltos para poder crear uno nuevo" : undefined}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: brand.primary }}
          >
            {showForm && !createBlocked ? "Cancelar" : "Nuevo ticket"}
          </button>
        </div>

        {createBlocked && pending && (
          <div
            role="alert"
            className={`mb-6 rounded-2xl border-2 p-5 ${isDarkMode ? "border-amber-500/60 bg-amber-500/10 text-amber-100" : "border-amber-400 bg-amber-50 text-amber-900"}`}
          >
            <p className="font-bold">Cierra tus tickets resueltos para poder crear uno nuevo</p>
            <p className="mt-1 text-sm">
              Tienes {pending.count} tickets resueltos sin cerrar y el máximo es {pending.limit}. Usa «Cerrar ticket» en los que ya
              quedaron resueltos; si alguno no quedó, ábrelo y márcalo para rehacer.
            </p>
          </div>
        )}
        {closeError && <div className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{closeError}</div>}

        {showForm && !createBlocked && (
          <form onSubmit={submit} className={`mb-6 space-y-3 rounded-2xl border p-5 shadow-sm ${cardCls}`}>
            {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="grid gap-3 sm:grid-cols-2">
              {isReseller ? (
                <div>
                  <Select
                    styles={selectStyles}
                    options={allCompanies.map((c) => ({ value: c.id, label: c.name || c.business_name }))}
                    value={form.companyId ? { value: form.companyId, label: companyLabel(form.companyId) } : null}
                    onChange={(opt) => setForm((f) => ({ ...f, companyId: opt?.value || "", affectedUserId: "" }))}
                    placeholder="Seleccionar compañía *"
                    isClearable={false}
                  />
                  <p className={`mt-1 text-xs ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>
                    Si el afectado (o quien reporta) es personal interno, usa la compañía <strong>{INTERNAL_COMPANY_NAME}</strong>.
                  </p>
                </div>
              ) : null}
              {form.companyId ? (
                <div>
                  <Select
                    styles={selectStyles}
                    options={companyUsers.map((u) => ({ value: u.id, label: userLabel(u) }))}
                    value={affectedUser ? { value: affectedUser.id, label: userLabel(affectedUser) } : null}
                    onChange={(opt) => setForm((f) => ({ ...f, affectedUserId: opt?.value || "" }))}
                    placeholder={companyUsersLoading ? "Cargando usuarios…" : "Usuario afectado *"}
                    isLoading={companyUsersLoading}
                    isDisabled={companyUsersLoading}
                    noOptionsMessage={() => "Esta compañía no tiene usuarios"}
                    isClearable={false}
                  />
                  {affectedUser ? (
                    <p className={`mt-1 text-xs ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>
                      Rol: <strong>{affectedUser.roleName || "sin rol asignado"}</strong>
                    </p>
                  ) : (
                    <p className={`mt-1 text-xs ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>
                      La persona a la que le ocurre el problema (no siempre quien abre el ticket).
                    </p>
                  )}
                </div>
              ) : !isReseller ? (
                <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Tu cuenta no tiene compañía asignada; contacta a soporte para poder abrir tickets.
                </p>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                styles={selectStyles}
                options={MODULE_OPTIONS.map((m) => ({ value: m.id, label: m.label }))}
                value={form.module ? { value: form.module, label: MODULE_OPTIONS.find(m => m.id === form.module)?.label || "" } : null}
                onChange={(opt) => setForm((f) => ({ ...f, module: opt?.value || "", moduleSection: "" }))}
                placeholder="Seleccionar módulo..."
                isClearable
              />
              {form.module && (MODULE_OPTIONS.find(m => m.id === form.module)?.sections?.length ?? 0) > 0 && (
                <Select
                  styles={selectStyles}
                  options={MODULE_OPTIONS.find(m => m.id === form.module)?.sections.map((s) => ({ value: s.id, label: s.label })) || []}
                  value={form.moduleSection ? { value: form.moduleSection, label: MODULE_OPTIONS.find(m => m.id === form.module)?.sections.find(s => s.id === form.moduleSection)?.label || "" } : null}
                  onChange={(opt) => setForm((f) => ({ ...f, moduleSection: opt?.value || "" }))}
                  placeholder="Seleccionar sección..."
                  isClearable
                />
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                styles={selectStyles}
                options={config?.brands.map((b) => ({ value: b.id, label: b.label })) || []}
                value={form.brand ? { value: form.brand, label: config?.brands.find(b => b.id === form.brand)?.label || "" } : null}
                onChange={(opt) => setForm((f) => ({ ...f, brand: opt?.value || "" }))}
                placeholder="Seleccionar marca..."
                isClearable={false}
              />
              <Select
                styles={selectStyles}
                options={config?.categories.map((c) => ({ value: c.id, label: c.label })) || []}
                value={form.category ? { value: form.category, label: config?.categories.find(c => c.id === form.category)?.label || "" } : null}
                onChange={(opt) => setForm((f) => ({ ...f, category: opt?.value || "" }))}
                placeholder="Seleccionar categoría..."
                isClearable={false}
              />
            </div>
            <input className={isDarkMode ? darkInputCls : lightInputCls} placeholder="Asunto" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <textarea className={isDarkMode ? darkInputCls : lightInputCls} rows={4} placeholder="Descripción" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                styles={selectStyles}
                options={[
                  { value: "low", label: "Baja" },
                  { value: "medium", label: "Media" },
                  { value: "high", label: "Alta" },
                  { value: "urgent", label: "Urgente" },
                ]}
                value={{ value: form.priority, label: ["Baja", "Media", "Alta", "Urgente"][["low", "medium", "high", "urgent"].indexOf(form.priority)] }}
                onChange={(opt) => setForm((f) => ({ ...f, priority: opt?.value as TicketPriority }))}
                placeholder="Seleccionar prioridad..."
                isClearable={false}
              />
              <input type="file" multiple className={`text-sm ${isDarkMode ? "text-white/70" : "text-gray-500"}`} onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </div>
            <button type="submit" disabled={creating} className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: brand.primary }}>
              {creating ? "Creando…" : "Crear ticket"}
            </button>
          </form>
        )}

        {loading ? (
          <p className={`text-center ${isDarkMode ? "text-white/70" : "text-gray-400"}`}>Cargando…</p>
        ) : tickets.length === 0 ? (
          <p className={`rounded-2xl border p-8 text-center text-sm ${isDarkMode ? "text-white/70" : "text-gray-400"} ${cardCls}`}>Aún no tienes tickets.</p>
        ) : (
          <div className="space-y-3">
            {(Array.isArray(tickets) ? tickets : []).map((t) => {
              const moduleLabel = t.module ? MODULE_OPTIONS.find(m => m.id === t.module)?.label : null;
              const moduleSectionLabel = t.moduleSection && t.module ? MODULE_OPTIONS.find(m => m.id === t.module)?.sections.find(s => s.id === t.moduleSection)?.label : null;
              const detailUrl = `/soporte/seguimiento?ref=${encodeURIComponent(t.reference)}&token=${encodeURIComponent(t.token || "")}`;
              // La tarjeta deja de ser un <Link> entero: el botón de cerrar no
              // puede ir dentro de un enlace, así que va en un pie aparte.
              return (
                <div key={t.id} className={`rounded-2xl border shadow-sm transition hover:shadow ${cardCls}`}>
                  <Link to={detailUrl} className="block p-4">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className={`text-xs font-bold tracking-wide ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>{t.reference}</span>
                      <span className="rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: statusFallback(t.status)?.color || brand.primary }}>{t.statusLabel || statusFallback(t.status)?.label || t.status}</span>
                      <ReworkChip count={t.rework?.count} />
                      <PriorityBadge priority={t.priority} />
                      <SlaBadge state={t.sla?.overall} />
                    </div>
                    <p className={`font-semibold ${textCls}`}>{t.title}</p>
                    <p className={`mt-0.5 text-xs ${isDarkMode ? "text-white/70" : "text-gray-400"}`}>
                      {t.brandLabel}
                      {moduleLabel && <> · {moduleLabel}{moduleSectionLabel && ` → ${moduleSectionLabel}`}</>}
                      · {formatDate(t.createdAt)}
                    </p>
                  </Link>
                  {t.status === PENDING_CLOSURE_STATUS && (
                    <div className={`flex flex-wrap items-center justify-between gap-3 border-t px-4 py-2.5 ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
                      <span className="text-xs font-semibold text-amber-600">¿Quedó resuelto? Falta que lo cierres.</span>
                      <span className="flex items-center gap-3">
                        <Link to={detailUrl} className={`text-xs underline ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>
                          No quedó resuelto
                        </Link>
                        <button
                          type="button"
                          onClick={() => closeTicket(t.reference)}
                          disabled={closingRef !== null}
                          className="rounded-full px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                          style={{ backgroundColor: brand.primary }}
                        >
                          {closingRef === t.reference ? "Cerrando…" : "Cerrar ticket"}
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const lightInputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200";
const darkInputCls = "w-full rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gray-200";
